import React, { useState, useEffect, useCallback, useContext } from "react";
import Dashboard from "../../ui/Dashboard";
import { AuthContext } from "../../../context/AuthContext";

const serverUrl = process.env.REACT_APP_serverUrl || "";

// Simple error box
const ErrorBox = ({ error, onRetry }) => (
  <div
    style={{
      padding: "1.25rem",
      border: "1px solid #f87171",
      borderRadius: 10,
      background: "#fef2f2",
    }}
  >
    <h3 style={{ margin: 0, color: "#b91c1c", fontSize: 17 }}>
      Failed to load Instructor Dashboard
    </h3>
    <button
      onClick={onRetry}
      style={{
        background: "#b91c1c",
        color: "#fff",
        padding: "0.5rem 1rem",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        fontWeight: 600,
      }}
    >
      Retry
    </button>
  </div>
);

const InstructorDashboard = ({
  instituteId: propInstituteId,
  instructorId: propInstructorId,
  pageSize = 25,
}) => {
  const { user } = useContext(AuthContext);

  const [instituteId, setInstituteId] = useState(propInstituteId || "");
  // instructorId always derived (unless prop provided)
  const derivedInstructorId = propInstructorId || user?._id || "";
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingInstitute, setPendingInstitute] = useState("");

  // Resolve instituteId: prop > user.instituteId > localStorage > URL
  useEffect(() => {
    if (propInstituteId) {
      setInstituteId(propInstituteId);
      return;
    }
    if (user?.instituteId) {
      setInstituteId(user.instituteId);
      localStorage.setItem("instituteId", user.instituteId);
      return;
    }
    const stored = localStorage.getItem("instituteId");
    if (stored) {
      setInstituteId(stored);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const q = params.get("instituteId");
    if (q) {
      setInstituteId(q);
      localStorage.setItem("instituteId", q);
    }
  }, [propInstituteId, user]);

  const handleError = useCallback((err) => {
    setLoadError(err?.message || "Unknown error");
  }, []);

  const handleLoadingChange = useCallback((val) => setLoading(val), []);

  const retry = () => {
    setLoadError(null);
    setInstituteId((p) => p);
  };

  const applyInstituteId = (e) => {
    e.preventDefault();
    const trimmed = pendingInstitute.trim();
    if (!trimmed) return;
    localStorage.setItem("instituteId", trimmed);
    setInstituteId(trimmed);
  };

  const ready = Boolean(instituteId && derivedInstructorId);

  if (!ready) {
    return (
      <div
        style={{
          maxWidth: 560,
          margin: "2.5rem auto",
          padding: "2rem",
          background: "#ffffff",
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Instructor Analytics</h2>
        {!instituteId && (
            <p style={{ color: "#374151", lineHeight: 1.5 }}>
              No institute resolved.{" "}
              {user?.instituteId
                ? "Applying your account institute..."
                : "Enter one below or add it to the user context."}
            </p>
        )}
        {!derivedInstructorId && (
          <p style={{ color: "#374151", lineHeight: 1.5 }}>
            Waiting for user context (user._id) to resolve instructorId.
          </p>
        )}
        {!instituteId && !user?.instituteId && (
          <form
            onSubmit={applyInstituteId}
            style={{ marginTop: "1rem", display: "flex", gap: "0.6rem" }}
          >
            <input
              type="text"
              placeholder="Enter instituteId"
              value={pendingInstitute}
              onChange={(e) => setPendingInstitute(e.target.value)}
              style={{
                flex: 1,
                padding: "0.65rem 0.75rem",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
              }}
            />
            <button
              type="submit"
              style={{
                background: "#2563eb",
                color: "#fff",
                padding: "0.65rem 1.1rem",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Save
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>

      {loadError && (
        <div style={{ marginBottom: "1.25rem" }}>
          <ErrorBox error={loadError} onRetry={retry} />
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 12,
            right: 12,
            background: "#111827",
            color: "#fff",
            padding: "0.55rem 0.9rem",
            fontSize: 12,
            borderRadius: 6,
            boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#60a5fa",
              animation: "pulse 1s ease-in-out infinite",
            }}
          />
          Loading…
        </div>
      )}

      <Dashboard
        role="instructor"
        instituteId={instituteId}
        actorId={derivedInstructorId}
        pageSize={pageSize}
        serverBaseUrl={serverUrl}
        onError={handleError}
        onLoadingChange={handleLoadingChange}
        initialFilters={{}}
        disableFilters={{
          instructor: true,
        }}
      />
    </div>
  );
};

export default InstructorDashboard;